import { websiteBuilderNormalizeFooter, websiteBuilderNormalizeHeader, websiteBuilderNormalizeRoot } from "pages/secure/facility/websiteBuilder/editor/schema/normalization/websiteBuilderNormalizeEditor";
import { IWebsiteBuilderWebsiteVersionPageRaw, TWebsiteBuilderWebsiteVersionPage, TWebsiteBuilderWebsiteVersionPageMigration } from "redux/reducers/models/websiteBuilder";
import { IWebsiteBuilderHeader } from "pages/secure/facility/websiteBuilder/editor/nodes/section/websiteBuilderHeader";
import { IWebsiteBuilderFooter } from "pages/secure/facility/websiteBuilder/editor/nodes/section/websiteBuilderFooter";
import { TFunction } from "i18next";
import { WebsiteBuilderMigrationError } from "pages/secure/facility/websiteBuilder/editor/schema/migrations/helpers/migrationError";
import { websiteBuilderMigrateToCurrentSchemaVersion } from "pages/secure/facility/websiteBuilder/editor/schema/migrations/websiteBuilderMigrateToCurrentSchemaVersion";
import { isWebsiteBuilderSchemaVersionMigrationNode } from "pages/secure/facility/websiteBuilder/editor/schema/migrations/helpers/migrationNode";

interface IEnforceSchemaInput {
  header?: unknown;
  footer?: unknown;
  page?: IWebsiteBuilderWebsiteVersionPageRaw;
  pages?: IWebsiteBuilderWebsiteVersionPageRaw[];
}

interface IEnforceSchemaOutput {
  enforcedHeader: IWebsiteBuilderHeader;
  enforcedFooter: IWebsiteBuilderFooter;
  enforcedPage: TWebsiteBuilderWebsiteVersionPage;
  enforcedPages: TWebsiteBuilderWebsiteVersionPage[];
}

type TEnforceSchemaIOMapping<T extends IEnforceSchemaInput> = (
  (T extends { header: unknown } ? { enforcedHeader: IEnforceSchemaOutput["enforcedHeader"] } : unknown) &
  (T extends { footer: unknown } ? { enforcedFooter: IEnforceSchemaOutput["enforcedFooter"] } : unknown) &
  (T extends { page: IWebsiteBuilderWebsiteVersionPageRaw } ? { enforcedPage: IEnforceSchemaOutput["enforcedPage"] } : unknown) &
  (T extends { pages: IWebsiteBuilderWebsiteVersionPageRaw[] } ? { enforcedPages: IEnforceSchemaOutput["enforcedPages"] } : unknown)
);

function enforceHeader(header: unknown): IWebsiteBuilderHeader {
  if (!isWebsiteBuilderSchemaVersionMigrationNode(header)) {
    throw new WebsiteBuilderMigrationError({ type: "undefined-schema-version" });
  }

  const migratedHeader = websiteBuilderMigrateToCurrentSchemaVersion(header);
  const normalizedHeader = websiteBuilderNormalizeHeader(migratedHeader);

  return normalizedHeader;
}

function enforceFooter(footer: unknown): IWebsiteBuilderFooter {
  if (!isWebsiteBuilderSchemaVersionMigrationNode(footer)) {
    throw new WebsiteBuilderMigrationError({ type: "undefined-schema-version" });
  }

  const migratedFooter = websiteBuilderMigrateToCurrentSchemaVersion(footer);
  const normalizedFooter = websiteBuilderNormalizeFooter(migratedFooter);

  return normalizedFooter;
}

function enforcePage(page: IWebsiteBuilderWebsiteVersionPageRaw): TWebsiteBuilderWebsiteVersionPage {
  if (!isWebsiteBuilderSchemaVersionMigrationNode(page.root)) {
    throw new WebsiteBuilderMigrationError({ type: "undefined-schema-version" });
  }

  const migratedPage: TWebsiteBuilderWebsiteVersionPageMigration = {
    ...page,
    root: websiteBuilderMigrateToCurrentSchemaVersion(page.root),
  };

  const normalizedPage: TWebsiteBuilderWebsiteVersionPage = {
    ...migratedPage,
    root: websiteBuilderNormalizeRoot(migratedPage.root),
  };

  return normalizedPage;
}

function enforcePages(pages: IWebsiteBuilderWebsiteVersionPageRaw[]): TWebsiteBuilderWebsiteVersionPage[] {
  const migratedPages: TWebsiteBuilderWebsiteVersionPageMigration[] = pages.map(page => {
    if (!isWebsiteBuilderSchemaVersionMigrationNode(page.root)) {
      throw new WebsiteBuilderMigrationError({ type: "undefined-schema-version" });
    }

    return {
      ...page,
      root: websiteBuilderMigrateToCurrentSchemaVersion(page.root),
    };
  });

  const normalizedPages: TWebsiteBuilderWebsiteVersionPage[] = migratedPages.map(page => {
    return {
      ...page,
      root: websiteBuilderNormalizeRoot(page.root),
    };
  });

  return normalizedPages;
}

type TWebsiteBuilderEnforceSchemaContent<T> = {
  type: "content";
  content: T;
}

type TWebsiteBuilderEnforceSchemaError = {
  type: "error";
  message: string;
}

type TWebsiteBuilderEnforceSchemaResult<T> = TWebsiteBuilderEnforceSchemaContent<T> | TWebsiteBuilderEnforceSchemaError;

export function websiteBuilderEnforceSchema<T extends IEnforceSchemaInput>(
  input: T,
  t: TFunction,
): TWebsiteBuilderEnforceSchemaResult<TEnforceSchemaIOMapping<T>> {
  try {
    const output: Partial<IEnforceSchemaOutput> = {};

    if (input.header !== undefined) {
      output.enforcedHeader = enforceHeader(input.header);
    }
    if (input.footer !== undefined) {
      output.enforcedFooter = enforceFooter(input.footer);
    }
    if (input.page !== undefined) {
      output.enforcedPage = enforcePage(input.page);
    }
    if (input.pages !== undefined) {
      output.enforcedPages = enforcePages(input.pages);
    }

    return { type: "content", content: output as TEnforceSchemaIOMapping<T> };
  } catch (e: unknown) {
    if (e instanceof WebsiteBuilderMigrationError) {
      return { type: "error", message: e.getMessage(t) };
    }

    return {
      type: "error",
      message: t("Error when validating website content."),
    };
  }
}